
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ChessAppLayout } from "@/components/ChessAppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Users, Clock, Pin } from "lucide-react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { useSEO } from '@/hooks/useSEO';

export default function Forums() {
  // SEO Configuration
  useSEO({
    title: "Chess Community Forums - Discussion & Tips | ChessHatch",
    description: "Join the ChessHatch community! Discuss chess strategies, share tips, ask questions, and connect with fellow chess enthusiasts from around the world.",
    keywords: "chess forums, chess community, chess discussion, chess tips, chess strategies, chess players community",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "DiscussionForumPosting",
      "name": "ChessHatch Community Forums",
      "description": "Community discussion platform for chess enthusiasts",
      "author": {
        "@type": "Organization",
        "name": "ChessHatch"
      }
    }
  });

  const { data: categories, isLoading } = useQuery({
    queryKey: ["forum-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("forum_categories")
        .select("*")
        .order("order_index");
      
      if (error) throw error;
      return data;
    },
  });

  const { data: recentTopics } = useQuery({
    queryKey: ["recent-topics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("forum_topics")
        .select(`
          *,
          forum_categories (name, slug),
          user_profiles (display_name)
        `)
        .order("last_posted_at", { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <ChessAppLayout>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            {[...Array(6)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  return (
    <ChessAppLayout>
      <div className="flex gap-6 p-6">
        {/* Main Content */}
        <div className="flex-1">
          <div className="mb-6">
            <h1 className="text-3xl font-bold mb-2">Community Forums</h1>
            <p className="text-muted-foreground">
              Connect with chess players around the world
            </p>
          </div>

          <div className="space-y-4">
            {categories?.map((category) => (
              <Card key={category.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <MessageSquare className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <Link 
                          to={`/forums/c/${category.slug}`}
                          className="text-lg font-semibold hover:text-primary transition-colors"
                        >
                          {category.name}
                        </Link>
                        <p className="text-sm text-muted-foreground">
                          {category.description}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <MessageSquare className="w-4 h-4" />
                        <span>{category.topics_count} topics</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{category.posts_count} posts</span>
                      </div>
                      {category.last_posted_at && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{formatDistanceToNow(new Date(category.last_posted_at), { addSuffix: true })}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-80">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Recent Topics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentTopics?.map((topic) => (
                  <div key={topic.id} className="border-b border-border pb-3 last:border-b-0 last:pb-0">
                    <div className="flex items-start gap-2 mb-1">
                      {topic.is_pinned && <Pin className="w-4 h-4 text-primary mt-0.5" />}
                      <Link 
                        to={`/forums/t/${topic.id}`}
                        className="text-sm font-medium hover:text-primary transition-colors line-clamp-2"
                      >
                        {topic.title}
                      </Link>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>in {topic.forum_categories?.name}</span>
                      <span>{formatDistanceToNow(new Date(topic.last_posted_at), { addSuffix: true })}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </ChessAppLayout>
  );
}
